import { NavigateFn, useSearch } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import CantileverButton from 'components/CantileverButton'
import { ActiveFilters } from 'components/table/enhancements/filters/ActiveFilters'
import { AddFilter } from 'components/table/enhancements/filters/AddFilter'
import isEqual from 'lodash/isEqual'
import React from 'react'
import styled from 'styled-components'
import { StyledCloseIcon } from 'styles/assetDatabaseStyles'
import { AssetDBSearchParams } from 'types'

const FilterBarContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  min-width: 0;
  align-items: center;
`

const FixedButtons = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
`

const ClearAllButton = styled(CantileverButton)`
  margin-left: auto;
`

export const FilterBar = <TData,>({
  columns,
  getColumn,
  initialColumnFilters,
  navigate,
  disableUrlFilters,
}: {
  columns: ColumnDef<TData>[]
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  initialColumnFilters?: ColumnFiltersState
  navigate: NavigateFn
  disableUrlFilters?: boolean
}) => {
  const search = useSearch({ strict: false })

  // Initialize activeFilters from both URL parameters and initialColumnFilters
  const [activeFilters, setActiveFilters] = React.useState<string[]>(() => {
    const urlFilterIds = Object.keys(search).filter((filterId) =>
      columns.some((col) => col.id === filterId)
    )
    const initialFilterIds = initialColumnFilters
      ? initialColumnFilters.map((filter) => filter.id)
      : []
    return [...new Set([...urlFilterIds, ...initialFilterIds])]
  })

  // Track previous URL filter IDs to avoid unnecessary updates
  const prevUrlFilterIdsRef = React.useRef<string[]>([])

  // Sync active filters with URL parameters on changes
  React.useEffect(() => {
    const urlFilterIds = Object.keys(search).filter((filterId) =>
      columns.some((col) => col.id === filterId)
    )

    // Only update if the URL filter IDs have actually changed
    if (!isEqual(urlFilterIds, prevUrlFilterIdsRef.current)) {
      setActiveFilters(urlFilterIds)
      prevUrlFilterIdsRef.current = urlFilterIds
    }
  }, [search, columns])

  const clearAll = () => {
    // Update the URL to remove all filters except view (only if URL filters are enabled)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => {
          const newSearch = { ...prev }
          activeFilters.forEach((filter) => {
            if (filter !== 'view') {
              newSearch[filter as keyof AssetDBSearchParams] = undefined
            }
          })
          return newSearch
        },
      })
    }

    // Then clear the active filters except view
    setActiveFilters(activeFilters.filter((filter) => filter === 'view'))

    // Finally remove the filters from the columns except view
    activeFilters.forEach((filter) => {
      if (filter !== 'view') {
        const column = getColumn(filter)
        if (column) {
          column.setFilterValue(undefined)
        }
      }
    })
  }

  return (
    <FilterBarContainer>
      <FixedButtons data-role='add-filter'>
        <AddFilter columns={columns} setActiveFilters={setActiveFilters} />
      </FixedButtons>
      {activeFilters.length > 0 && (
        <ActiveFilters
          columns={columns}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          getColumn={getColumn}
          initialColumnFilters={initialColumnFilters}
          navigate={navigate}
          disableUrlFilters={disableUrlFilters}
        />
      )}
      <FixedButtons>
        {activeFilters.some((filter) => filter !== 'view') && (
          <ClearAllButton ghost onClick={clearAll}>
            Clear Filters
            <StyledCloseIcon />
          </ClearAllButton>
        )}
      </FixedButtons>
    </FilterBarContainer>
  )
}
